'use client'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSortDown } from 'react-icons/fa'

import hafizLogo from '@/assets/images/logo.png'
import { Button } from '@/blocks/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/blocks/components/ui/popover'
import { publicEnv } from '@/config/public-env'
import { cn } from '@/lib/utils'
import { PublicHomeDto } from '@/server/public-api/types'
import { Locale } from '@/types'
import { buildLocalePath } from '@/utils'

import HeaderTop from './HeaderTop'
import MobileNav from './MobileNav'
import { getNavbarItems } from './utils'

const Header = ({ lang, home }: { lang: Locale; home: PublicHomeDto }) => {
  const pathname = usePathname()
  const [isPopOverOpen, setPopOverOpen] = useState(false)
  const { t } = useTranslation()

  const hoverTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const navbarItems = getNavbarItems(pathname, lang, home)

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setPopOverOpen(true)
    }, 400)
  }

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setPopOverOpen(false)
  }

  return (
    <header>
      <HeaderTop academy={home.academy} />
      <section className='flex items-center bg-nutral-light py-1 md:min-h-[100px]'>
        <div className='container flex items-center justify-between'>
          <Link href={buildLocalePath(lang, '/')}>
            <figure className='flex items-center gap-2 sm:gap-4'>
              <Image
                src={home.academy.logoUrl ? `${publicEnv.NEXT_PUBLIC_R2_BASE_URL}/${home.academy.logoUrl}` : hafizLogo}
                alt='logo'
                width={89}
                height={75}
                className='h-[56px] w-auto max-w-[120px] object-contain sm:h-[75px]'
              />
              <figcaption className='text-lg font-semibold capitalize text-secondary sm:text-2xl'>
                {home.academy.name || t('footer.logo.title')}
              </figcaption>
            </figure>
          </Link>

          <nav className='hidden md:block'>
            <ul className='flex items-center gap-4'>
              {navbarItems.map(navItem =>
                navItem.subItems ? (
                  <li
                    key={navItem.href}
                    className={getNavItemStyle(navItem.isActive ?? false)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Popover open={isPopOverOpen} onOpenChange={setPopOverOpen}>
                      <PopoverTrigger onMouseEnter={handleMouseEnter}>
                        <div
                          className={cn('flex cursor-pointer  gap-2', {
                            'hover:bg-tertiary': isPopOverOpen,
                          })}
                        >
                          {t(navItem.title)}
                          {navItem?.subItems && <FaSortDown className='mt-1 size-3' />}
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className='!w-[200px] p-2' onCloseAutoFocus={(e: Event) => e.preventDefault()}>
                        <ul className=' divide-y divide-primary'>
                          {navItem?.subItems?.map(subItem => (
                            <li key={subItem.href}>
                              <Link href={subItem.href} className='block cursor-pointer px-1 py-3 hover:bg-tertiary'>
                                {t(subItem.title)}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </PopoverContent>
                    </Popover>
                  </li>
                ) : (
                  <li key={navItem.href} className={getNavItemStyle(navItem.isActive ?? false)}>
                    <Link href={navItem.href} className='flex cursor-pointer  gap-2'>
                      {t(navItem.title)}
                      {navItem?.subItems && <FaSortDown className='mt-1 size-3' />}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </nav>
          <div className='space-x-4 max-sm:ms-auto'>
            <Button
              asChild
              className='font-semibold text-black hover:bg-secondary hover:text-white max-sm:h-8 max-sm:px-3 max-sm:text-sm'
            >
              <Link href={buildLocalePath(lang, '/register')}>{t('nav.free_trial')}</Link>
            </Button>
          </div>

          <MobileNav home={home} />
        </div>
      </section>
    </header>
  )
}

export default Header

const getNavItemStyle = (isPathnameActive: boolean) =>
  cn('rounded-md p-[10px] text-xl font-medium text-black hover:bg-tertiary', {
    'bg-tertiary': isPathnameActive,
  })
